/**
 * Two-sided referral helpers.
 *
 * Flow:
 *   1. Any page load with `?ref=CODE` (or `?referrer=CODE`) stashes the code
 *      in localStorage under `fev.referrer_code` — via `captureRefFromUrl()`
 *      called from App.js.
 *   2. Auth pages call `consumeRef()` at register/session time to send the
 *      code along and CLEAR it (one-shot).
 */

const KEY = "fev.referrer_code";

/**
 * Read `?ref=` (or `?referrer=`) from the current URL. If found, stash it in
 * localStorage. Idempotent — subsequent visits with a fresh code overwrite.
 * Does not mutate the URL (avoids fighting react-router).
 */
export function captureRefFromUrl() {
  try {
    const qs = new URLSearchParams(window.location.search || "");
    const code = (qs.get("ref") || qs.get("referrer") || "").trim().toUpperCase();
    if (code && /^[A-Z0-9]{4,32}$/.test(code)) {
      window.localStorage.setItem(KEY, code);
    }
  } catch {
    // ignore — SSR / private mode / etc.
  }
}

/**
 * Return the pending referral code (if any) WITHOUT clearing it. Useful for
 * showing "You're being referred by …" chips before the user actually signs up.
 */
export function peekRef() {
  try {
    return window.localStorage.getItem(KEY) || null;
  } catch {
    return null;
  }
}

/**
 * Return the pending referral code AND clear it. Call this once per successful
 * register/session request so the code is only spent once.
 */
export function consumeRef() {
  try {
    const v = window.localStorage.getItem(KEY);
    if (v) window.localStorage.removeItem(KEY);
    return v || null;
  } catch {
    return null;
  }
}
